import { api } from "./api";

// Notificações do Windows (Web Push) neste navegador, no mesmo modelo do Portal de Chamados:
// o service worker /sw-notificacoes.js recebe os avisos do servidor e mostra no canto da tela,
// mesmo com a aba do SGQ fechada.

const SW_URL = "/sw-notificacoes.js";

export type WindowsNotificationState = "unsupported" | "blocked" | "inactive" | "active";

export function supportsWindowsNotifications() {
  return typeof window !== "undefined" && window.isSecureContext && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

async function registration() {
  const current = await navigator.serviceWorker.getRegistration(SW_URL);
  if (current) return current;
  await navigator.serviceWorker.register(SW_URL);
  return navigator.serviceWorker.ready;
}

/** Chave VAPID (base64url) no formato que o PushManager pede. */
function keyToBytes(base64url: string) {
  const base64 = (base64url + "=".repeat((4 - (base64url.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

function sameKey(current: ArrayBuffer | null, next: Uint8Array) {
  if (!current) return false;
  const bytes = new Uint8Array(current);
  return bytes.length === next.length && bytes.every((byte, index) => byte === next[index]);
}

export async function windowsNotificationState(): Promise<WindowsNotificationState> {
  if (!supportsWindowsNotifications()) return "unsupported";
  if (Notification.permission === "denied") return "blocked";
  const current = await navigator.serviceWorker.getRegistration(SW_URL);
  const subscription = await current?.pushManager.getSubscription();
  if (!subscription || Notification.permission !== "granted") return "inactive";
  // O navegador pode estar inscrito sem o servidor saber (ex.: a API caiu na hora de ativar).
  // Reenviar é idempotente; se o servidor não confirmar, mostra como inativo para ativar de novo.
  try {
    await api.subscribeWindowsNotifications(subscription.toJSON());
    return "active";
  } catch {
    return "inactive";
  }
}

/** Pede a permissão do navegador (se preciso), inscreve e avisa o servidor. */
export async function enableWindowsNotifications(publicKey: string): Promise<WindowsNotificationState> {
  if (!supportsWindowsNotifications()) return "unsupported";
  const permission = await Notification.requestPermission();
  if (permission === "denied") return "blocked";
  if (permission !== "granted") return "inactive";
  const current = await registration();
  const key = keyToBytes(publicKey);
  let subscription = await current.pushManager.getSubscription();
  // Inscrição feita com outra chave do servidor não recebe mais nada: refaz.
  if (subscription && !sameKey(subscription.options.applicationServerKey, key)) {
    await subscription.unsubscribe();
    subscription = null;
  }
  subscription ??= await current.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  await api.subscribeWindowsNotifications(subscription.toJSON());
  return "active";
}

export async function disableWindowsNotifications(): Promise<WindowsNotificationState> {
  const current = await navigator.serviceWorker.getRegistration(SW_URL);
  const subscription = await current?.pushManager.getSubscription();
  if (subscription) {
    await api.unsubscribeWindowsNotifications(subscription.endpoint).catch(() => undefined);
    await subscription.unsubscribe();
  }
  return "inactive";
}
